'use client';

import { clearStoredReferralCode, getStoredReferralCode } from './referral';

const STORAGE_KEY = 'mix_auth_token';

export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

export function setStoredToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) {
    window.localStorage.setItem(STORAGE_KEY, token);
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent('mix_auth_change'));
}

/**
 * On initial load, the Yandex callback lands at "/#auth_token=...".
 * Strip it out of the URL and persist the token.
 */
export function consumeAuthTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash.startsWith('#auth_token=')) return null;
  const token = decodeURIComponent(hash.slice('#auth_token='.length));
  setStoredToken(token);
  const clean = window.location.pathname + window.location.search;
  window.history.replaceState(null, '', clean);
  return token;
}

export interface MeResponse {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  photoUrl: string | null;
  balance: string;
  referralBalance: string;
  referralCode: string;
  signupSource: string;
  identities: Array<{
    provider: string;
    email: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  }>;
  subscription: null | {
    planName: string;
    status: string;
    generationsUsed: number;
    generationsLimit: number;
    currentPeriodEnd: string;
  };
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? '';

export async function fetchMe(token: string): Promise<MeResponse | null> {
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const json = (await res.json()) as { data?: MeResponse };
  return json.data ?? null;
}

// Each signup endpoint accepts an optional `referralCode` — applied
// server-side only when a brand-new user is created. We always read the
// stored code on call (it may have been captured on a previous landing)
// and clear it after a successful response so it doesn't get re-sent on
// the next login from the same browser.
function withReferral<T extends Record<string, unknown>>(payload: T): T & {
  referralCode?: string;
} {
  const code = getStoredReferralCode();
  return code ? { ...payload, referralCode: code } : payload;
}

async function consumeAuthResponse(
  res: Response,
): Promise<{ token: string } | null> {
  if (!res.ok) return null;
  const json = (await res.json()) as { data?: { token: string } };
  if (json.data?.token) clearStoredReferralCode();
  return json.data ?? null;
}

export async function loginWithVk(payload: {
  access_token: string;
  user_id: number | string;
  email?: string;
}): Promise<{ token: string } | null> {
  const res = await fetch(`${API_BASE}/api/auth/vk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withReferral(payload)),
  });
  return consumeAuthResponse(res);
}

export async function loginWithYandexToken(
  access_token: string,
): Promise<{ token: string } | null> {
  const res = await fetch(`${API_BASE}/api/auth/yandex/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withReferral({ access_token })),
  });
  return consumeAuthResponse(res);
}

export async function loginWithTelegramWidget(
  payload: Record<string, unknown>,
): Promise<{ token: string } | null> {
  const res = await fetch(`${API_BASE}/api/auth/telegram/web`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withReferral(payload)),
  });
  return consumeAuthResponse(res);
}
